import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { createLogger } from '../utils/logger';
import { usePerformancePhase } from './usePerformancePhase';

const logger = createLogger({ module: 'useQuarterlyPriorities' });

export interface QuarterlyPriority {
  id: string;
  focus_id: string;
  quarter: string;
  year: number;
  priority_title: string;
  priority_description?: string;
  evidence_type?: string;
  icon?: string;
  is_completed: boolean;
  completion_date?: string;
  completion_notes?: string;
  linked_entity_id?: string;
  linked_entity_type?: string;
  is_system_generated: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

interface PrioritySuggestion {
  title: string;
  description?: string;
  icon?: string;
}

interface UseQuarterlyPrioritiesResult {
  priorities: QuarterlyPriority[];
  loading: boolean;
  error: Error | null;
  currentQuarter: string;
  suggestions: PrioritySuggestion[];
  loadingSuggestions: boolean;
  addPriority: (title: string, description?: string) => Promise<QuarterlyPriority | null>;
  updatePriority: (id: string, updates: Partial<QuarterlyPriority>) => Promise<boolean>;
  toggleComplete: (id: string) => Promise<boolean>;
  deletePriority: (id: string) => Promise<boolean>;
  fetchSuggestions: () => Promise<void>;
  refreshPriorities: () => Promise<void>;
}

// Get current quarter info
const getCurrentQuarterInfo = (date: Date = new Date()): { quarter: string; year: number } => {
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed
  const quarter = Math.floor(month / 3) + 1;
  return { quarter: `Q${quarter}`, year };
};

export const useQuarterlyPriorities = (focusId: string, focus?: any): UseQuarterlyPrioritiesResult => {
  const { user } = useAuth();
  const [priorities, setPriorities] = useState<QuarterlyPriority[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [suggestions, setSuggestions] = useState<PrioritySuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const { quarter: currentQuarter, year: currentYear } = getCurrentQuarterInfo();
  
  // Get current phase from focus
  const { currentPhase } = usePerformancePhase(focus);

  // Fetch priorities
  const fetchPriorities = useCallback(async () => {
    if (!focusId || !user?.id) {
      setPriorities([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('quarterly_priorities')
        .select('*')
        .eq('focus_id', focusId)
        .eq('quarter', currentQuarter)
        .eq('year', currentYear)
        .order('is_completed', { ascending: true })
        .order('order_index', { ascending: true })
        .order('created_at', { ascending: true });

      if (fetchError) throw fetchError;

      setPriorities(data || []);
    } catch (err) {
      logger.error('Failed to fetch quarterly priorities', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [focusId, user?.id, currentQuarter, currentYear]);

  // Fetch suggestions based on current phase (doesn't create database records)
  const fetchSuggestions = useCallback(async () => {
    if (!currentPhase) return;

    try {
      setLoadingSuggestions(true);
      
      // Fetch templates from database
      const { data: templates, error: templateError } = await supabase
        .from('quarterly_priority_templates')
        .select('*')
        .eq('phase', currentPhase)
        .eq('is_active', true)
        .order('order_index', { ascending: true });

      if (templateError) throw templateError;

      if (!templates || templates.length === 0) {
        logger.warn('No priority templates found for phase:', currentPhase);
        setSuggestions([]);
        return;
      }
      
      // Convert templates to suggestions (not database records)
      const prioritySuggestions: PrioritySuggestion[] = templates.map(template => ({
        title: template.priority_title,
        description: template.priority_description,
        icon: template.icon
      }));

      setSuggestions(prioritySuggestions);
    } catch (err) {
      logger.error('Failed to fetch priority suggestions', err);
      setError(err as Error);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [currentPhase]);

  // Add new priority
  const addPriority = useCallback(async (title: string, description?: string): Promise<QuarterlyPriority | null> => {
    if (!focusId || !user?.id) return null;

    try {
      const newPriority = {
        focus_id: focusId,
        quarter: currentQuarter,
        year: currentYear,
        priority_title: title,
        priority_description: description,
        is_system_generated: false,
        order_index: priorities.filter(p => !p.is_completed).length
      };

      const { data, error: insertError } = await supabase
        .from('quarterly_priorities')
        .insert(newPriority)
        .select()
        .single();

      if (insertError) throw insertError;

      setPriorities(prev => [...prev, data]);
      return data;
    } catch (err) {
      logger.error('Failed to add priority', err);
      setError(err as Error);
      return null;
    }
  }, [focusId, user?.id, currentQuarter, currentYear, priorities]);

  // Update priority
  const updatePriority = useCallback(async (id: string, updates: Partial<QuarterlyPriority>): Promise<boolean> => {
    try {
      const { error: updateError } = await supabase
        .from('quarterly_priorities')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('focus_id', focusId);

      if (updateError) throw updateError;

      setPriorities(prev => prev.map(p => 
        p.id === id ? { ...p, ...updates } : p
      ));
      
      return true;
    } catch (err) {
      logger.error('Failed to update priority', err);
      setError(err as Error);
      return false;
    }
  }, [focusId]);

  // Toggle completion status
  const toggleComplete = useCallback(async (id: string): Promise<boolean> => {
    const priority = priorities.find(p => p.id === id);
    if (!priority) return false;

    const updates = {
      is_completed: !priority.is_completed,
      completion_date: !priority.is_completed ? new Date().toISOString() : null
    };

    return updatePriority(id, updates);
  }, [priorities, updatePriority]);

  // Delete priority
  const deletePriority = useCallback(async (id: string): Promise<boolean> => {
    try {
      const { error: deleteError } = await supabase
        .from('quarterly_priorities')
        .delete()
        .eq('id', id)
        .eq('focus_id', focusId);

      if (deleteError) throw deleteError;

      setPriorities(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (err) {
      logger.error('Failed to delete priority', err);
      setError(err as Error);
      return false;
    }
  }, [focusId]);

  // Initial fetch
  useEffect(() => {
    fetchPriorities();
  }, [fetchPriorities]);

  return {
    priorities,
    loading,
    error,
    currentQuarter: `${currentYear}-${currentQuarter}`,
    suggestions,
    loadingSuggestions,
    addPriority,
    updatePriority,
    toggleComplete,
    deletePriority,
    fetchSuggestions,
    refreshPriorities: fetchPriorities
  };
};